import { Component, Input, OnChanges } from '@angular/core';
import { ColorPalette, PageSection } from '../../../models/end-client-reporting-package';
import { ChartSeries, ReportBlock, chartColors, tint } from '../report-aggregation';
import { CategoryMark, categoriesOf } from './row-categories';
import { axisMaxFor } from './chart-layout';
import { DEFAULT_TOP_RANKS, RankRow, readRanks } from './rank-shares';

/**
 * The most tiers worth drawing.
 *
 * Each band carries its own colour and its own key, and past four the colours stop reading as one
 * ordered scheme and start reading as four unrelated charts -- which is the thing the shared scale
 * and the shared tracks exist to prevent. Four is also as many priority bands as a client meeting
 * can hold in its head at once.
 */
export const MAX_TIERS = 4;

/** Attributes per tier when nothing says otherwise, which is what the default count works out to. */
export const IDEAL_TIER_SIZE = 5;

/** How far the light half of a bar is taken toward white from its tier's colour. */
const REST_TINT = 0.45;

/**
 * Share of the track kept clear at its right-hand end, for the two figures to sit in.
 *
 * The bars are scaled into what is left rather than the figures being pushed past the track: the
 * track is the backdrop the reading happens against, and a figure outside it reads as a separate
 * column to look up rather than as this row's number. A share rather than a pixel width because the
 * module cannot measure the track -- it is a grid column of a card of unknown width -- and every bar
 * shares one scale, so one reserve serves every row.
 */
const FIGURE_RESERVE = 0.22;

/**
 * The usual four, in order. Free text on the block, because what a tier is called is the study's
 * own language -- these are a starting point, not a vocabulary.
 */
export const DEFAULT_TIER_LABELS = [
  'Primary Drivers',
  'Decision Drivers',
  'Supporting Factors',
  'Lower Priority',
];


/**
 * How far past 100% the rank-one shares may total before the module says these are not one ranking.
 *
 * One-sided on purpose. Each respondent has exactly one first choice, so across a whole grid the
 * shares total 100 -- but a deck may chart a subset of the rows, which legitimately totals less,
 * and rows asked of different subsets have different bases, which moves it either way by a few
 * points. Only a total far *above* 100 is impossible, and that is the misconfiguration worth
 * catching: a set of unrelated single-choice questions, where every first answer counts as a #1.
 */
const RANK_ONE_TOTAL_TOLERANCE = 10;

/**
 * How many bands to draw: what the block asks for, or one per five attributes where it says nothing.
 *
 * Clamped rather than refused, and never more bands than there are rows -- four tiers over three
 * attributes is three headings and an empty one, which reads as missing data rather than as a
 * setting nobody adjusted.
 */
export function tierCountFor(tierCount: number | undefined, rows: number): number {
  const asked = tierCount === undefined
    ? Math.ceil(rows / IDEAL_TIER_SIZE)
    : Math.trunc(tierCount);
  return Math.max(1, Math.min(asked, MAX_TIERS, Math.max(rows, 1)));
}

/**
 * Whether this block will draw a card per band, which it only does with more than one band to draw.
 *
 * Asked by the block host before the component exists, so it is answered from the block rather than
 * from the rendered tiers. One tier is an ordinary module -- a single card with the block's own
 * heading at the top of it -- and putting that heading outside a lone card would leave it floating
 * above a panel for no reason.
 */
export function rankedDriverDrawsOwnCards(block: PageSection): boolean {
  const extra = block.reportExtraData as { tierCount?: number; variableKeys?: string[] };
  return tierCountFor(extra.tierCount, extra.variableKeys?.length ?? 0) > 1;
}

/** One attribute of the grid. */
interface DriverRow {
  attribute: string;
  /** Its category's colour, where one claims it. Blank leaves the row on the tier's two shades. */
  category?: string;
  categoryLight?: string;
  base: number;
  /** Share ranking it first, and the share ranking it anywhere in the top few. Raw, for the widths. */
  rankOne: number;
  top: number;
  /** Rounded once, here, so the bar and the printed figures can never disagree. */
  rankOneText: string;
  topText: string;
  /** Widths as a share of the track, which is the axis max rather than 100%. */
  oneWidth: number;
  restWidth: number;
  /** Read in place of the bar, which carries the two figures visually. */
  summary: string;
}

/** A priority band: a fixed number of attributes, in sorted order. */
interface Tier {
  label: string;
  rows: DriverRow[];
  /** The tier's own colour, carried by the card's leading edge -- the bars no longer use it. */
  edge: string;
  ink: string;
  light: string;
}

/**
 * Ranked driver: what matters most, out of a ranking grid. One row per attribute, sorted by the
 * share putting it in the top few and cut into named priority tiers.
 *
 * Each bar carries two figures at once -- its length is the top-N share, and the solid segment at
 * its start is the share ranking that attribute *first*. That is the whole design: an attribute
 * many people put somewhere in their top three is broadly agreed on, while one with a long solid
 * head is the one people lead with, and those are not the same finding.
 *
 * Drawn in CSS rather than Highcharts, like the head-to-head module and for the same reason: tier
 * bands interleaved between categories, a figure inside one segment and another past the end of the
 * bar are a layout problem rather than a charting one, and every one of them is a fight against a
 * category axis.
 */
@Component({
  selector: 'app-ranked-driver-block',
  standalone: false,
  template: `
    <div class="rd" [class.compact]="compact" [class.single]="tiers.length === 1">
      @for (tier of tiers; track $index) {
        <section class="tier">
          <div class="panel">
            <!-- The tier's own colour is dropped where categories are set: the bars are carrying a
                 theme then, and a second colour system beside them is one more than a reader can
                 hold. The tier keeps its name and its ordering, which is what it is for. -->
            @if (tier.label) {
              <span class="tier-name" [style.color]="categoryKey.length ? null : tier.edge">
                @if (!categoryKey.length) {
                  <i class="tier-mark" [style.background]="tier.edge" aria-hidden="true"></i>
                }{{ tier.label }}
              </span>
            }
          </div>

          @for (row of tier.rows; track row.attribute) {
            <span class="attr grey5">
              @if (row.category) {
                <i class="cat-dot" [style.background]="row.category" aria-hidden="true"></i>
              }{{ row.attribute }}
            </span>

            <!-- One scale for every bar in the module, so a length means the same thing in the
                 bottom tier as in the top one. -->
            <span class="bar" role="img" [attr.aria-label]="row.summary">
              <span class="seg one" [style.width.%]="row.oneWidth"
                    [class.is-whole]="!row.restWidth"
                    [style.background]="row.category || tier.ink"></span>
              <!-- The full top-N length, not the remainder: it is drawn underneath the solid
                   bar rather than after it, so there is no join for a rounded end to gap against. -->
              <span class="seg rest" [style.width.%]="row.oneWidth + row.restWidth"
                    [style.background]="row.categoryLight || tier.light"></span>
              <!-- At the end of its own bar rather than in a column of its own: read against the
                   bar it belongs to, a figure a column away is a second thing to look up. The axis
                   already carries a step of headroom past the longest bar for it to sit in. -->
              <!-- Both figures here, the way the prototype prints them: the top-N share in bold
                   and the rank-one share after it, quieter. The rank-one figure used to sit inside
                   the solid bar, which meant it was dropped entirely on any row whose bar was too
                   short to hold it -- the low-ranking drivers, where the reader most wants to know
                   whether anything ranked it first at all. -->
              <span class="fig" [style.left.%]="row.oneWidth + row.restWidth">
                <b>{{ row.topText }}</b><span class="fig-rank">· #1 {{ row.rankOneText }}</span>
              </span>
            </span>
          }
        </section>
      }

      <!-- One key for the module, not one per tier. The bars say which reading -- ranked first, or
           ranked anywhere in the top few -- and those two mean the same thing in every band, so they
           are the same two colours throughout and can be explained once. Which band a row is in is
           carried by the tier's name and the colour down its card's edge. -->
      <div class="module-key">
        <span class="key">
          <i class="dot" [style.background]="readingInk" aria-hidden="true"></i>{{ rankOneLegend }}
        </span>
        <span class="key">
          <i class="dot" [style.background]="readingLight" aria-hidden="true"></i>{{ topLegend }}
        </span>
      </div>

      <!-- Named where the bars are themed. Below the reading key rather than beside it: one says
           what a shade means and the other what a hue means, and they are read one after the other,
           not together. -->
      @if (categoryKey.length) {
        <div class="module-key cat-key">
          <span class="key cat-key-name">Category key</span>
          @for (mark of categoryKey; track mark.label) {
            <span class="key">
              <i class="dot" [style.background]="mark.colour" aria-hidden="true"></i>{{ mark.label }}
            </span>
          }
        </div>
      }

      @for (problem of problems; track problem) {
        <p class="note grey4">{{ problem }}</p>
      }
    </div>
  `,
  styles: [`
    :host { display: block; }

    .rd { font-size: 13px; }
    .rd.compact { font-size: 11px; }

    /* Every tier draws the same four tracks, none of them content-sized, so the bars start and end
       in the same place in every band. A track sized to its own tier's text would give each band its
       own geometry, and a length could only be compared inside one band. */
    .tier {
      display: grid;
      /* The deck's bar-chart split: 46% to the answers, the rest to the bars -- see BAR_LABEL_SHARE
         in chart-layout.ts. These bars are drawn in CSS rather than by Highcharts, so the share is
         restated here, but a reader moving between this module and a bar chart sees one layout.
         The band's name used to hold a third column of its own on the left, which pushed every row
         across and lined this module's bars up with nothing else on the page. */
      grid-template-columns: 46% 1fr;
      align-items: center;
      column-gap: 10px;
      row-gap: 7px;
      /* A card per band, and the only card here: the block host draws none for this module (see
         SELF_CARDED_BLOCKS), so each tier is its own white panel on the page rather than a panel
         inside a bigger one. The tier's own colour runs down its edge -- it is the only thing tying
         a band to the bars inside it, so the card carries it rather than leaving it to the key. */
      background: var(--ecr-card, #ffffff);
      border: 1px solid var(--ecr-line, #dbe4e8);
      border-radius: var(--ecr-radius, 8px);
      padding: 14px 16px;
    }
    .tier + .tier { margin-top: 10px; }

    /* One band is one module: the block host keeps its own card in that case, so the band must not
       draw a second one inside it, and the tier colour has nothing to distinguish it from. */
    .rd.single .tier {
      background: transparent;
      border: 0;
      border-radius: 0;
      padding: 0;
    }
    .rd.single .module-key { padding-left: 0; }
    .rd.compact .tier {
      grid-template-columns: 46% 1fr;
      column-gap: 7px;
      row-gap: 5px;
      border-radius: 6px;
      padding: 10px 11px;
    }
    .rd.compact .tier + .tier { margin-top: 7px; }

    .panel {
      /* Across the top of the band, above the rows it heads, rather than beside them. */
      grid-column: 1 / -1;
      /* Top, not centred. It held a name and a two-line key when it was centred against the rows;
         with the key moved out to the module it is a heading, and a heading sits at the top of the
         thing it heads. */
      align-self: start;
      display: flex;
      flex-direction: column;
      gap: 3px;
      padding-right: 10px;
    }
    /* Larger than the figures it presides over: the band's name is a heading. Its colour is bound
       inline from the tier, matching the card's leading edge -- with the bars no longer drawn in
       the band's colour, the name and the edge are what tell one band from another. */
    .tier-name {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 17px;
      font-weight: 700;
      line-height: 1.2;
      margin-bottom: 14px;
    }
    /* The band's colour, beside its name instead of down the whole card. The card reads as a card
       like every other one on the page, and the colour sits where the thing it identifies is -- it
       used to be a 6px edge the length of the band, which was a lot of paint for a label. */
    .tier-mark {
      flex: 0 0 auto;
      width: 4px;
      /* Just past the cap height, so it reads as a rule beside the name rather than as a letter in
         it. Relative so it tracks the heading down to the compact size. */
      height: 1.25em;
    }
    .rd.compact .tier-name { font-size: 13px; margin-bottom: 9px; gap: 6px; }
    .rd.compact .tier-mark { width: 3px; }

    .key {
      display: flex;
      align-items: center;
      font-size: 11px;
      font-weight: 600;
      color: var(--ecr-ink, #1b2b33);
      line-height: 1.3;
    }
    .rd.compact .key { font-size: 10px; }
    /* Square, like the deck's other keys. A key's swatch stands for a colour; the round dot
       beside a row stands for that row, and keeping the two shapes apart is what stops the key
       reading as one more row of the chart. */
    .dot {
      flex: none;
      width: 8px;
      height: 8px;
      border-radius: 2px;
      margin-right: 5px;
    }
    /* Round, against the key's square: this one marks a row rather than naming a colour.
       Inline-block rather than flex, since it sits inside the label, which wraps. */
    .cat-dot {
      border-radius: 50%;
      display: inline-block;
      height: 7px;
      margin-right: 6px;
      width: 7px;
    }
    /* Under the reading key and set apart from it: one says what a shade means, the other what a
       hue means, and run together they read as one long key of six unrelated things. The rule is
       what makes them two lists rather than one. */
    .cat-key-name {
      font-weight: 700;
    }
    .cat-key {
      border-top: 1px solid var(--ecr-line, #dbe4e8);
      margin-top: 0.6rem;
      padding-top: 0.6rem;
    }

    .attr {
      grid-column: 1;
      line-height: 1.3;
    }

    /* A track behind every bar, like the rest of the deck's bar charts: the bars share one scale,
       so the unfilled part of a row is what says how far short of the longest it falls. This used
       to be left empty on the grounds that the remainder carried no reading -- true of a bar with
       a scale of its own, not of one measured against its neighbours. */
    .bar {
      grid-column: 2;
      display: flex;
      height: 17px;
      min-width: 0;
      /* The deck's bar corner, on the bar rather than on its segments. The two segments used to
         carry a corner each -- left on the solid one, right on the remainder -- which left the end
         square on any row where the remainder was zero width, and made the radius a thing two
         rules had to agree about. Clipped here, it is one rule and it cannot disagree. */
      border-radius: 3px;
      background: var(--ecr-bg, #eef3f5);
      position: relative; /* the bars and the figure are positioned against it */
    }
    .rd.compact .bar { height: 14px; }

/* Both bars start at the left edge and are stacked, not laid end to end: each is a rounded bar
       in its own right, and the shorter one sits on top. Butting them together meant one bar's
       rounded end met the next one's square start, which leaves a sliver of track showing through
       at every join however the corners are set. Layering has no join at all. */
    .seg {
      position: absolute;
      left: 0;
      top: 0;
      height: 100%;
      border-radius: 3px;
    }
    .seg.rest { z-index: 1; }
    /* Square where the solid bar ends over the paler one: the two read as one bar, rounded only at
       its ends. Rounded both ends when there is no paler part to meet. */
    .seg.one { z-index: 2; border-radius: 3px 0 0 3px; }
    .seg.one.is-whole { border-radius: 3px; }

    /* Parked at the end of the longer bar rather than at the end of the track: the figure belongs
       to the bar, and pinned to the right it drifted further from the bar the shorter the bar got,
       until on a low row it was reading as a column of its own. FIGURE_RESERVE is what keeps it
       inside the track once it has been pushed right on the longest row. */
    .fig {
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      z-index: 3;
      padding-left: 7px;
      white-space: nowrap;
      color: var(--ecr-ink, #1b2b33);
      font-size: var(--ecr-font-xs, 11px);
    }
    /* The second figure is the supporting one: same line, no weight of its own, and set back so the
       bold top-N share stays the number the eye lands on. */
    .fig-rank {
      color: var(--ecr-mut, #5a6b73);
      font-weight: 400;
      margin-left: 2px;
    }
    .rd.compact .fig { padding-left: 5px; font-size: var(--ecr-font-micro, 9px); }

    /* Under the last tier, aligned with them. Muted, because it explains the chart rather than
       being part of the reading -- and set well clear of it, since 10px left the key looking like
       another row of the chart rather than a note about it. */
    .module-key {
      display: flex;
      flex-wrap: wrap;
      gap: 14px;
      margin-top: 22px;
      padding-left: 16px;
      color: var(--ecr-mut, #5a6b73);
    }
    .rd.compact .module-key { gap: 10px; margin-top: 15px; padding-left: 11px; }

    .note {
      font-size: 0.85em;
      line-height: 1.35;
      margin: 6px 0 0;
    }
  `],
})
export class RankedDriverBlockComponent implements ReportBlock, OnChanges {
  @Input({ required: true }) block!: PageSection;
  @Input({ required: true }) series!: ChartSeries[];
  @Input({ required: true }) palette!: ColorPalette;
  @Input() compact?: boolean;

  tiers: Tier[] = [];
  /** Rows that cannot be drawn, and why. Listed under the module rather than tiered among them. */
  problems: string[] = [];
  /** The two bar colours, shared by every tier: they encode the reading, not the band. */
  barInk = '';
  barLight = '';
  /** The themes actually drawn, for the key under the module. Empty where none are set. */
  categoryKey: CategoryMark[] = [];
  /**
   * The reading key's two swatches: what "ranked first" and "ranked in the top few" look like.
   *
   * The bars' own colours while nothing is themed. Themed, they go neutral -- the bars are carrying
   * a hue per theme by then, so a key in the palette's first colour both matches nothing in
   * particular and collides with whichever theme was given that same first colour. Light against
   * dark is the whole of what this key says, and neutrals say it without claiming a theme.
   */
  readingInk = '';
  readingLight = '';

  /**
   * What the two shades of every bar mean.
   *
   * Categorised, these name the *shades* rather than two colours -- "Darker = ranked #1". Each bar
   * draws that pair in its own category's colour, so a swatch here is a colour that is on no bar at
   * all; saying darker and lighter is the only version of this key that is true of every row.
   * Uncategorised, every bar really is these two colours and the swatches speak for themselves.
   */
  rankOneLegend = 'Ranked #1';
  topLegend = '';

  ngOnChanges() {
    this.build();
  }

  private build() {
    this.tiers = [];
    this.problems = [];
    if (this.block.reportType !== 'tiered-ranking') return;
    const extra = this.block.reportExtraData;

    // The arithmetic is shared with the ownership matrix, which shows the same top-N share as a
    // column -- see rank-shares.ts. What is this module's own is everything below: the order, the
    // tiers and the bars.
    const reading = readRanks(this.series, extra);
    this.problems = [...reading.problems];
    if (!reading.rankCount) return;

    const categorised = !!this.block.reportExtraData
      && this.block.reportType === 'tiered-ranking'
      && !!this.block.reportExtraData.categories?.some(category => category.items?.length);
    this.rankOneLegend = categorised ? 'Darker = ranked #1' : 'Ranked #1';
    this.topLegend = categorised
      ? `Lighter = ranked in top ${reading.topRanks}`
      : `Ranked in Top ${reading.topRanks}`;

    // A row's key is the one at its own index in variableKeys, since seriesForBlock builds a
    // series per entry in order and rank-shares keeps that order. Resolved before the sort below.
    const keys = this.block.reportType === 'tiered-ranking'
      ? this.block.reportExtraData.variableKeys ?? []
      : [];
    const keyOf = new Map(reading.rows.map((row, at) => [row.attribute, keys[at] ?? row.attribute]));

    const categories = categoriesOf(
      this.block.reportType === 'tiered-ranking' ? this.block.reportExtraData.categories : undefined,
      [...keyOf.values()],
      this.palette);
    this.categoryKey = categories.legend;
    this.readingInk = categories.legend.length ? 'var(--ecr-ink, #26414c)' : this.palette.primary;
    this.readingLight = categories.legend.length
      ? 'var(--ecr-grey, #c3ced3)'
      : tint(this.palette.primary, REST_TINT);

    // A row no category claims goes inert once any category exists. Left on the tier's colour it
    // would be the palette's first role -- which is also the first category's, so "not in a
    // category" and "in the first one" came out identical.
    const unclaimed = categories.legend.length ? 'var(--ecr-grey, #c3ced3)' : undefined;

    const rows = reading.rows
      .map(row => this.rowFor(row, label => categories.colourOf(keyOf.get(label) ?? label) ?? unclaimed))
      // The order is the finding: most-chosen first, and where two are level the one people lead
      // with more often goes above. A stable third key is deliberate -- without it two identical
      // rows swap places between renders for no reason a reader could name, and with fixed-size
      // tiers that swap can move an attribute into another band.
      .sort((a, b) => b.top - a.top || b.rankOne - a.rankOne
        || a.attribute.localeCompare(b.attribute));

    if (!rows.length) return;

    // Each respondent has exactly one first choice, so this cannot legitimately run far over 100.
    const rankOneTotal = rows.reduce((sum, row) => sum + row.rankOne, 0);
    if (rankOneTotal > 100 + RANK_ONE_TOTAL_TOLERANCE) {
      this.problems.push('These questions are not one ranking: the #1 shares total '
        + `${Math.round(rankOneTotal)}%, and a respondent has only one first choice.`);
    }

    // The same axis rule every other chart here uses, so the headroom the end figure needs is the
    // headroom it gets -- the top-N figure sits past the end of the longest bar.
    const scaleMax = axisMaxFor([{
      name: '',
      points: rows.map(row => ({ label: '', value: 0, percent: row.top, optionIndex: 0 })),
      base: 0,
      multiResponse: false,
      known: true,
    }]);
    // Into the track minus the figures' reserve, so the longest bar stops short of them rather
    // than running under them.
    const usable = 100 * (1 - FIGURE_RESERVE);
    for (const row of rows) {
      row.oneWidth = (row.rankOne / scaleMax) * usable;
      row.restWidth = Math.max(0, ((row.top - row.rankOne) / scaleMax) * usable);
    }

    this.tiers = this.tiersOf(rows, extra.tierCount, extra.tierLabels);
  }

  /** A counted row, as this module draws it. */
  private rowFor(row: RankRow, colourOf: (label: string) => string | undefined): DriverRow {
    const category = colourOf(row.attribute);
    return {
      attribute: row.attribute,
      category,
      // The same ramp the tiers use between their two shades, so "ranked first" and "ranked in the
      // top few" mean the same distance apart whichever colour is carrying them.
      categoryLight: category ? tint(category, REST_TINT) : undefined,
      base: row.base,
      rankOne: row.rankOne,
      top: row.top,
      rankOneText: `${Math.round(row.rankOne)}%`,
      topText: `${Math.round(row.top)}%`,
      oneWidth: 0,
      restWidth: 0,
      summary: `${row.attribute}: ${Math.round(row.rankOne)}% ranked it first, `
        + `${Math.round(row.top)}% ranked it in the top few. n=${row.base}`,
    };
  }

  /**
   * The bands, as a count rather than a cut at a percentage.
   *
   * Deliberately not a threshold: this deck has a live filter rail, so a percentage boundary moves
   * attributes between tiers whenever a client filters, and a tier would mean something different
   * in every view of the same report. A fixed count of the sorted list moves an attribute only when
   * it actually overtakes another one.
   */
  private tiersOf(rows: DriverRow[], tierCount?: number, labels?: string[]): Tier[] {
    const count = tierCountFor(tierCount, rows.length);
    const chunks: DriverRow[][] = [];

    // Split as evenly as the rows allow, the larger bands first. Uneven the other way round would
    // put the widest band at the bottom, where a deck reads the least carefully.
    const size = Math.floor(rows.length / count);
    const spare = rows.length % count;
    let taken = 0;
    for (let index = 0; index < count; index++) {
      const take = size + (index < spare ? 1 : 0);
      chunks.push(rows.slice(taken, taken + take));
      taken += take;
    }

    // Ten distinct colours rather than the palette's five roles cycled, so a sixth tier is not a
    // repeat of the first. Used for the card's edge only now: when every band drew its bars in its
    // own colour, the same two readings had a different colour in every band and the key had to be
    // repeated inside each one to say so.
    const edges = chartColors(this.palette, Math.max(chunks.length, 1));
    this.barInk = this.palette.primary;
    this.barLight = tint(this.palette.primary, REST_TINT);

    return chunks.map((chunk, index) => ({
      // A single band has no name to show: one tier is "draw them all together", and a heading over
      // the only band would be the card title said twice.
      label: count > 1
        ? (labels?.[index]?.trim() || DEFAULT_TIER_LABELS[index] || `Tier ${index + 1}`)
        : '',
      rows: chunk,
      edge: edges[index],
      ink: this.barInk,
      light: this.barLight,
    }));
  }
}
